import { Box, Button, ChakraSelect, Flex, Text } from '@elkaso-app/web-design';
import { useSearchSuppliersApi } from 'apis/supplier';
import type { MatrixSupplier } from 'pages/prices/utils/build-price-matrix';
import { useMemo, useState } from 'react';

export interface AddSupplierControlProps {
  // Suppliers already shown as columns — hidden from the picker.
  excludeIds: number[];
  isOpen: boolean;
  onOpen: () => void;
  onCancel: () => void;
  onAdd: (supplier: MatrixSupplier) => void;
}

interface SupplierSelectOption {
  value: number;
  label: string;
}

// The "+ Add another supplier's price" affordance under the matrix (design
// handoff): a dashed trigger that expands into an inline picker. Confirming
// appends an empty supplier column — the first price typed into it creates
// the exclusive deal (and the supplier product, when missing) on the BE.
export const AddSupplierControl = ({ excludeIds, isOpen, onOpen, onCancel, onAdd }: AddSupplierControlProps) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<SupplierSelectOption | null>(null);

  const { data, isLoading } = useSearchSuppliersApi(search, { enabled: search.length >= 2 });

  const options = useMemo(
    () =>
      (data?.data ?? [])
        .filter((supplier) => !excludeIds.includes(supplier.id))
        .map((supplier) => ({ value: supplier.id, label: supplier.name })),
    [data, excludeIds]
  );

  const reset = () => {
    setSearch('');
    setSelected(null);
  };

  if (!isOpen) {
    return (
      <Button
        variant='outline'
        size='sm'
        fontSize='13px'
        fontWeight='medium'
        color='gray.600'
        borderStyle='dashed'
        borderColor='gray.300'
        borderRadius='9px'
        bg='white'
        onClick={onOpen}>
        + Add another supplier&rsquo;s price
      </Button>
    );
  }

  return (
    <Flex
      display='inline-flex'
      align='center'
      gap='8px'
      flexWrap='wrap'
      rowGap='8px'
      p='10px 12px'
      border='1px solid'
      borderColor='gray.200'
      borderRadius='10px'
      bg='white'>
      <Text fontSize='12.5px' color='gray.600' whiteSpace='nowrap'>
        Add column:
      </Text>
      <Box minW={{ base: '100%', md: '240px' }}>
        <ChakraSelect
          size='sm'
          options={options}
          value={selected}
          isLoading={isLoading}
          placeholder='Search suppliers…'
          // Portal the menu to body so the matrix scroll container can't clip it.
          menuPortalTarget={document.body}
          menuPosition='fixed'
          styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
          onInputChange={(input: string) => setSearch(input)}
          filterOption={() => true}
          onChange={(option: any) => setSelected(option ?? null)}
          noOptionsMessage={() => (search.length < 2 ? 'Type at least 2 characters' : 'No suppliers found')}
        />
      </Box>
      <Button
        size='sm'
        colorScheme='red'
        fontSize='12.5px'
        borderRadius='8px'
        isDisabled={!selected}
        onClick={() => {
          if (!selected) return;
          onAdd({ id: selected.value, name: selected.label });
          reset();
        }}>
        Add
      </Button>
      <Button
        size='sm'
        variant='ghost'
        fontSize='12.5px'
        color='gray.500'
        onClick={() => {
          reset();
          onCancel();
        }}>
        Cancel
      </Button>
    </Flex>
  );
};
